// FieldValidationBuilder.jsx
// Created by Claude on 2025-11-23
// Purpose: Field-level validation rule builder for form fields
// Allows creating custom validation rules with conditions and actions

import React, { useState, useEffect } from 'react';
import { createLogger } from '../../utils/logger';

const logger = createLogger("FieldValidationBuilder");

const FieldValidationBuilder = ({
  formFields = [],
  existingRule = null,
  onSave,
  onCancel
}) => {
  // Initialize state
  const [rule, setRule] = useState(
    existingRule || {
      id: '',
      name: '',
      description: '',
      conditions: [
        {
          id: 1,
          logicalOperator: 'IF',
          fieldId: '',
          operator: '',
          value: ''
        }
      ],
      thenAction: 'SHOW_ERROR',
      thenMessage: '',
      elseAction: 'ALLOW',
      elseMessage: '',
      status: 'ACTIVE'
    }
  );

  // Operator definitions by field type
  const OPERATORS = {
    text: ['EQUALS', 'NOT_EQUALS', 'CONTAINS', 'NOT_CONTAINS', 'IS_EMPTY', 'IS_NOT_EMPTY', 'STARTS_WITH', 'ENDS_WITH'],
    email: ['EQUALS', 'NOT_EQUALS', 'CONTAINS', 'IS_EMPTY', 'IS_NOT_EMPTY'],
    number: ['EQUALS', 'NOT_EQUALS', 'GREATER_THAN', 'LESS_THAN', 'GREATER_OR_EQUAL', 'LESS_OR_EQUAL', 'BETWEEN'],
    tel: ['EQUALS', 'NOT_EQUALS', 'CONTAINS', 'IS_EMPTY', 'IS_NOT_EMPTY'],
    password: ['EQUALS', 'NOT_EQUALS', 'MIN_LENGTH', 'MAX_LENGTH', 'IS_EMPTY', 'IS_NOT_EMPTY'],
    dropdown: ['EQUALS', 'NOT_EQUALS', 'IN', 'NOT_IN', 'IS_EMPTY', 'IS_NOT_EMPTY'],
    select: ['EQUALS', 'NOT_EQUALS', 'IN', 'NOT_IN', 'IS_EMPTY', 'IS_NOT_EMPTY'],
    checkbox: ['IS_CHECKED', 'IS_NOT_CHECKED'],
    date: ['EQUALS', 'NOT_EQUALS', 'BEFORE', 'AFTER', 'BETWEEN'],
    textarea: ['EQUALS', 'NOT_EQUALS', 'CONTAINS', 'NOT_CONTAINS', 'IS_EMPTY', 'IS_NOT_EMPTY', 'MIN_LENGTH', 'MAX_LENGTH']
  };

  // Action options
  const ACTIONS = [
    { value: 'SHOW_ERROR', label: 'Show Error Message', icon: '❌' },
    { value: 'SHOW_WARNING', label: 'Show Warning', icon: '⚠️' },
    { value: 'HIDE_FIELD', label: 'Hide Field', icon: '👁️' },
    { value: 'DISABLE_FIELD', label: 'Disable Field', icon: '🔒' },
    { value: 'ALLOW', label: 'Allow (No Action)', icon: '✅' }
  ];

  // Condition management
  const addCondition = () => {
    setRule({
      ...rule,
      conditions: [
        ...rule.conditions,
        {
          id: rule.conditions.length + 1,
          logicalOperator: 'AND',
          fieldId: '',
          operator: '',
          value: ''
        }
      ]
    });
  };

  const updateCondition = (id, updates) => {
    setRule({
      ...rule,
      conditions: rule.conditions.map(c => c.id === id ? { ...c, ...updates } : c)
    });
  };

  const removeCondition = (id) => {
    if (rule.conditions.length > 1) {
      setRule({
        ...rule,
        conditions: rule.conditions.filter(c => c.id !== id)
      });
    }
  };

  const getFieldType = (fieldId) => {
    const field = formFields.find(f => f.id === fieldId);
    return field?.type || field?.smartFieldType || 'text';
  };

  const getAvailableOperators = (fieldId) => {
    const fieldType = getFieldType(fieldId);
    return OPERATORS[fieldType] || OPERATORS.text;
  };

  const handleSave = () => {
    // Validation
    if (!rule.name || !rule.name.trim()) {
      alert('❌ Please provide a validation rule name');
      return;
    }

    const hasIncompleteCondition = rule.conditions.some(
      c => !c.fieldId || !c.operator || (needsValue(c.operator) && !c.value)
    );

    if (hasIncompleteCondition) {
      alert('❌ Please complete all condition fields');
      return;
    }

    if (rule.thenAction !== 'ALLOW' && !rule.thenMessage.trim()) {
      alert('❌ Please provide a THEN message');
      return;
    }

    // Generate ID if new
    const ruleData = {
      ...rule,
      id: rule.id || `FVAL_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };

    logger.info("handleSave", "Saving field validation rule", { name: ruleData.name });
    onSave(ruleData);
  };

  const needsValue = (operator) => {
    return !['IS_EMPTY', 'IS_NOT_EMPTY', 'IS_CHECKED', 'IS_NOT_CHECKED'].includes(operator);
  };

  return (
    <div style={{
      backgroundColor: '#FFFFFF',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      maxWidth: '900px',
      margin: '0 auto'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '600', color: '#111827' }}>
          {existingRule ? 'Edit' : 'Create'} Field Validation Rule
        </h2>
        <p style={{ margin: 0, fontSize: '14px', color: '#6B7280' }}>
          Define validation logic for form fields with conditions and actions
        </p>
      </div>

      {/* Rule Name & Description */}
      <div style={{ marginBottom: '24px' }}>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
          Rule Name *
        </label>
        <input
          type="text"
          value={rule.name}
          onChange={(e) => setRule({ ...rule, name: e.target.value })}
          placeholder="e.g., Age Must Be 18+"
          style={{
            width: '100%',
            padding: '10px 12px',
            fontSize: '14px',
            border: '1px solid #D1D5DB',
            borderRadius: '8px',
            marginBottom: '12px'
          }}
        />

        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
          Description (Optional)
        </label>
        <textarea
          value={rule.description}
          onChange={(e) => setRule({ ...rule, description: e.target.value })}
          placeholder="Describe what this validation checks..."
          rows={2}
          style={{
            width: '100%',
            padding: '10px 12px',
            fontSize: '14px',
            border: '1px solid #D1D5DB',
            borderRadius: '8px',
            resize: 'vertical'
          }}
        />
      </div>

      {/* Conditions Section */}
      <div style={{
        padding: '16px',
        backgroundColor: '#F9FAFB',
        borderRadius: '8px',
        border: '1px solid #E5E7EB',
        marginBottom: '20px'
      }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: '600', color: '#111827' }}>
          Conditions
        </h3>

        {rule.conditions.map((condition, index) => {
          const availableOperators = getAvailableOperators(condition.fieldId);
          const showValueInput = needsValue(condition.operator);

          return (
            <div key={condition.id} style={{ marginBottom: '12px' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: condition.logicalOperator === 'IF' ? '80px 1fr 1fr 1fr 40px' : '80px 1fr 1fr 1fr 40px',
                gap: '8px',
                alignItems: 'center'
              }}>
                {/* Logical Operator */}
                <select
                  value={condition.logicalOperator}
                  onChange={(e) => updateCondition(condition.id, { logicalOperator: e.target.value })}
                  disabled={index === 0}
                  style={{
                    padding: '8px',
                    fontSize: '13px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontWeight: '600',
                    color: condition.logicalOperator === 'IF' ? '#059669' : '#3B82F6',
                    backgroundColor: '#FFFFFF'
                  }}
                >
                  <option value="IF">IF</option>
                  <option value="AND">AND</option>
                  <option value="OR">OR</option>
                </select>

                {/* Field Selection */}
                <select
                  value={condition.fieldId}
                  onChange={(e) => updateCondition(condition.id, { fieldId: e.target.value, operator: '', value: '' })}
                  style={{
                    padding: '8px',
                    fontSize: '13px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px'
                  }}
                >
                  <option value="">Select field...</option>
                  {formFields.map(field => (
                    <option key={field.id} value={field.id}>
                      {field.label || field.id} ({field.type})
                    </option>
                  ))}
                </select>

                {/* Operator Selection */}
                <select
                  value={condition.operator}
                  onChange={(e) => updateCondition(condition.id, { operator: e.target.value })}
                  disabled={!condition.fieldId}
                  style={{
                    padding: '8px',
                    fontSize: '13px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px'
                  }}
                >
                  <option value="">Select operator...</option>
                  {availableOperators.map(op => (
                    <option key={op} value={op}>
                      {op.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>

                {/* Value Input */}
                {showValueInput ? (
                  <input
                    type="text"
                    value={condition.value}
                    onChange={(e) => updateCondition(condition.id, { value: e.target.value })}
                    placeholder="Value..."
                    style={{
                      padding: '8px',
                      fontSize: '13px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px'
                    }}
                  />
                ) : (
                  <div style={{ padding: '8px', fontSize: '13px', color: '#9CA3AF' }}>
                    (no value needed)
                  </div>
                )}

                {/* Remove Button */}
                <button
                  onClick={() => removeCondition(condition.id)}
                  disabled={rule.conditions.length === 1}
                  style={{
                    padding: '8px',
                    fontSize: '16px',
                    backgroundColor: 'transparent',
                    border: '1px solid #FCA5A5',
                    borderRadius: '6px',
                    cursor: rule.conditions.length === 1 ? 'not-allowed' : 'pointer',
                    color: '#DC2626'
                  }}
                  title="Remove condition"
                >
                  🗑️
                </button>
              </div>
            </div>
          );
        })}

        {/* Add Condition Button */}
        <button
          onClick={addCondition}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 12px',
            fontSize: '13px',
            fontWeight: '600',
            color: '#3B82F6',
            backgroundColor: '#EFF6FF',
            border: '1px solid #3B82F6',
            borderRadius: '6px',
            cursor: 'pointer',
            marginTop: '8px'
          }}
        >
          ➕ Add Condition
        </button>
      </div>

      {/* THEN Action */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
          THEN (If conditions are TRUE)
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '8px' }}>
          <select
            value={rule.thenAction}
            onChange={(e) => setRule({ ...rule, thenAction: e.target.value })}
            style={{
              padding: '10px 12px',
              fontSize: '14px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px'
            }}
          >
            {ACTIONS.map(action => (
              <option key={action.value} value={action.value}>
                {action.icon} {action.label}
              </option>
            ))}
          </select>

          {rule.thenAction !== 'ALLOW' && (
            <input
              type="text"
              value={rule.thenMessage}
              onChange={(e) => setRule({ ...rule, thenMessage: e.target.value })}
              placeholder="Enter message to display..."
              style={{
                padding: '10px 12px',
                fontSize: '14px',
                border: '1px solid #D1D5DB',
                borderRadius: '8px'
              }}
            />
          )}
        </div>
      </div>

      {/* ELSE Action */}
      <div style={{ marginBottom: '24px' }}>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
          ELSE (If conditions are FALSE)
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '8px' }}>
          <select
            value={rule.elseAction}
            onChange={(e) => setRule({ ...rule, elseAction: e.target.value })}
            style={{
              padding: '10px 12px',
              fontSize: '14px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px'
            }}
          >
            {ACTIONS.map(action => (
              <option key={action.value} value={action.value}>
                {action.icon} {action.label}
              </option>
            ))}
          </select>

          {rule.elseAction !== 'ALLOW' && (
            <input
              type="text"
              value={rule.elseMessage}
              onChange={(e) => setRule({ ...rule, elseMessage: e.target.value })}
              placeholder="Enter message to display..."
              style={{
                padding: '10px 12px',
                fontSize: '14px',
                border: '1px solid #D1D5DB',
                borderRadius: '8px'
              }}
            />
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '16px', borderTop: '1px solid #E5E7EB' }}>
        <button
          onClick={onCancel}
          style={{
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            backgroundColor: '#F3F4F6',
            border: '1px solid #D1D5DB',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          style={{
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: '600',
            color: '#FFFFFF',
            backgroundColor: '#3B82F6',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          💾 Save Validation Rule
        </button>
      </div>
    </div>
  );
};

export default FieldValidationBuilder;


